import { messaging } from "../../../firebase-init";
import secure_axios from "../../services/secure_axios";

const initialState = () => ({
  isNotificationEnabled: false,
  fcmToken: null,
  topics: [],
  vapidKey: "BKF4lA9KeQslzkwuwxFCiToNh5ak7dsk0JVCWzOMLEKD_8AfgWdMcGNVrk20KTdQ8RhyScHS3_tKlw38fS9PsNM",
  isIOS: false,
  notifications: [],
  messages: [],
  drawerOpen: false,
});

const state = initialState();

const getters = {
  allNotifications: (state) => [...state.notifications, ...state.messages],
  unreadNotificationsCount: (state) => state.notifications.filter(n => !n.read).length,
  unreadMessagesCount: (state) => state.messages.filter(m => !m.read).length,
};

const mutations = {
  setNotificationEnabled(state, isEnabled) {
    state.isNotificationEnabled = isEnabled;
  },
  setFCMToken(state, token) {
    state.fcmToken = token;
  },
  setTopics(state, topics) {
    state.topics = topics;
  },
  setIsIOS(state, value) {
    state.isIOS = value;
  },
  ADD_NOTIFICATION(state, notification) {
    state.notifications.unshift({
      ...notification,
      read: false,
      type: 'notification'
    });
  },
  ADD_MESSAGE(state, message) {
    state.messages.unshift({
      ...message,
      read: false,
      type: 'message'
    });
  },
  MARK_AS_READ(state, { id, type }) {
    const items = type === 'message' ? state.messages : state.notifications;
    const item = items.find(i => i.id === id);
    if (item) item.read = true;
  },
  SET_DRAWER_OPEN(state, isOpen) {
    state.drawerOpen = isOpen;
  },
  RESET_STATE(state) {
    Object.assign(state, initialState());
  },
};

const actions = {
  async initializeNotifications({ dispatch }) {
    await dispatch('checkPlatform');
    await dispatch('checkPushNotificationPermission');
    if (state.isNotificationEnabled) {
      await dispatch('setupMessageListener');
      await dispatch('refreshToken');
    }
  },

  checkPlatform({ commit }) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    commit('setIsIOS', isIOS);
  },

  async requestNotificationPermission({ state, commit, dispatch }) {
    if (state.isIOS) {
      console.log('iOS detected. Push notifications are not fully supported.');
      commit('setNotificationEnabled', false);
      dispatch('showIOSNotificationMessage');
      return;
    }

    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await messaging.getToken({ vapidKey: state.vapidKey });
        commit('setFCMToken', token);
        commit('setNotificationEnabled', true);
        await dispatch('updateTokenOnServer', { token, operation: 'add' });
        dispatch('setupMessageListener');
      } else {
        throw new Error('Permission denied');
      }
    } catch (err) {
      console.error("Unable to get permission to notify.", err);
      commit('setNotificationEnabled', false);
    } finally {
      dispatch('checkPushNotificationPermission');
    }
  },

  async disableNotifications({ state, commit, dispatch }) {
    try {
      if (state.fcmToken) {
        await messaging.deleteToken(state.fcmToken);
        await dispatch('updateTokenOnServer', { token: state.fcmToken, operation: 'remove' });
      }
      commit('setNotificationEnabled', false);
      commit('setFCMToken', null);
    } catch (err) {
      console.error("Unable to delete token. ", err);
    } finally {
      dispatch('checkPushNotificationPermission');
    }
  },

  async refreshToken({ state, commit, dispatch }) {
    try {
      const refreshedToken = await messaging.getToken({ vapidKey: state.vapidKey });
      console.log('Token refreshed.');
      commit('setFCMToken', refreshedToken);
      await dispatch('updateTokenOnServer', { token: refreshedToken, operation: 'update' });
    } catch (err) {
      console.error('Unable to retrieve refreshed token ', err);
    }
  },

  async updateTokenOnServer(_, { token, operation }) {
    try {
      await secure_axios.post('/notification/fcm-token', { FCMTOKEN: token, operation });
      console.log(`Token ${operation}d on server successfully`);
    } catch (error) {
      console.error(`Error ${operation}ing token on server:`, error);
    }
  },

  async addTopic({ dispatch }, topicName) {
    await secure_axios.post('/notification/topics/add', { topicName });
    dispatch('getTopics');
  },

  async toggleSubscription({ dispatch }, { topicName, subscribe }) {
    const action = subscribe ? 'subscribe' : 'unsubscribe';
    await secure_axios.post(`/notification/${action}`, { topicName });
    dispatch('getTopics');
  },

  async getTopics({ commit }) {
    const response = await secure_axios.get('/notification/topics');
    commit('setTopics', response.data.topics);
  },

  async removeTopic({ dispatch }, topicName) {
    await secure_axios.post('/notification/topics/remove', { topicName });
    dispatch('getTopics');
  },

  setupMessageListener({ dispatch }) {
    messaging.onMessage((payload) => {
      console.log('Message received. ', payload);
      dispatch('handleIncomingMessage', payload);
    });
  },

  handleIncomingMessage({ commit }, payload) {
    console.log('Handling incoming message', payload);
    // Determine if it's a notification or a message based on the payload
    if (payload.notification) {
      commit('ADD_NOTIFICATION', {
        id: Date.now().toString(),
        title: payload.notification.title,
        body: payload.notification.body,
        timestamp: new Date(),
        data: payload.data,
      });
    } else {
      commit('ADD_MESSAGE', {
        id: Date.now().toString(),
        title: payload.data.title || 'New Message',
        body: payload.data.body,
        timestamp: new Date(),
        data: payload.data,
      });
    }
  },

  checkPushNotificationPermission({ commit }) {
    if ('Notification' in window) {
      commit('setNotificationEnabled', Notification.permission === 'granted');
    } else {
      commit('setNotificationEnabled', false);
    }
  },

  toggleNotificationDrawer({ commit, state }) {
    commit('SET_DRAWER_OPEN', !state.drawerOpen);
  },

  markAsRead({ commit }, { id, type }) {
    commit('MARK_AS_READ', { id, type });
  },

  showIOSNotificationMessage() {
    // Implement a method to show a message for iOS users
    console.log("Push notifications are not fully supported on iOS. Please check for updates manually.");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};